import { AnyAction } from 'redux';
import { ApplicationState } from 'app/types-store';
import { ActionDispatching } from 'app/types-shared';
import { complexValidationFromServer } from 'app/infrastructure/data/validation';
import { ErrorNotification } from 'app/infrastructure/notifications/user-messages';

import { mergeSavedReportsWithEvaluations } from './utils/merge-saved-reports';
import {
  RequestReportEvaluation, ReceiveReportEvaluation, FailReportEvaluation,
  SelectCurrentSavedReports, UpdateCurrentSavedReports, UpdateCurrentValidation,
  RequestAddSavedReports, ReceiveAddSavedReports, FailAddSavedReports,
  UpdateSavedReport, DeleteSavedReport, DeleteAllSavedReports,
  SwitchDisplayDisabled, UpdateSavedReportValidation
} from './action-types';
import { api } from './api';
import { SavedReportsEndpoints } from './constants';
import { GenericSavedReport, SavedReportsCommonState, SavedReportsValidation, SavedReportValidation } from './types-store';
import { OnUpdateSavedReport } from 'app/area/shared/saved-reports/types';

// eslint:disable-next-line: max-func-body-length
export const actionCreators = <SavedReport extends GenericSavedReport>(
  component: string,
  endpoints: SavedReportsEndpoints,
  normalizeSequence: (items: SavedReport[]) => SavedReport[]
) => ({

  loadSavedReportEvaluation: (
    typeId: number,
    favouriteId: number,
    onUpdateSavedReport: OnUpdateSavedReport<SavedReport>,
    onSavedReportError: () => void,
    getSavedReportsState: (state: ApplicationState) => SavedReportsCommonState<SavedReport>
  ): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestReportEvaluation(component, typeId, favouriteId));

      try {
        const evaluation = await api.loadSavedReportEvaluation(endpoints, favouriteId);

        const hasValidationErrors = Object.keys(evaluation?.validationErrors ?? {}).length > 0;

        dispatch(new ReceiveReportEvaluation(component, typeId, favouriteId,
          { value: evaluation?.code?.v, description: evaluation?.code?.d },
          evaluation?.report,
          evaluation?.editable,
          hasValidationErrors
            ? complexValidationFromServer(evaluation.validationErrors)
            : {}
        ));

        const savedReportsState = getSavedReportsState(getState());

        if (savedReportsState?.typeId === typeId &&
          savedReportsState?.currentSavedReport?.code?.value === favouriteId) {

          if (!hasValidationErrors) {
            onUpdateSavedReport(savedReportsState?.currentSavedReport, savedReportsState.currentValidation);
          } else {
            onSavedReportError();
          }
        }

      } catch (err) {

        const errorMessage = err && (err.userMessages || err.message);
        !!errorMessage && dispatch(new ErrorNotification(errorMessage));

        dispatch(new FailReportEvaluation(component, typeId, favouriteId));
      }
    },

  selectCurrentSavedReports: (savedReport: SavedReport, onlyIdMode: boolean): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new SelectCurrentSavedReports(component, savedReport, onlyIdMode));
    },

  updateCurrentSavedReports: (savedReport: Partial<SavedReport>): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateCurrentSavedReports(component, savedReport));
    },

  updateCurrentValidation: (validation: SavedReportValidation<SavedReport>): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateCurrentValidation(component, validation));
    },

  addSavedReports: (
    savedReports: SavedReport[],
    sequence: number
  ): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      if ((savedReports?.length ?? 0) === 0) {
        return;
      }

      try {
        dispatch(new RequestAddSavedReports(component));

        const evaluationsWire = await api.loadSavedReportEvaluations(endpoints,
          savedReports.map(report => report.code?.value));

        dispatch(new ReceiveAddSavedReports(
          component,
          mergeSavedReportsWithEvaluations(savedReports, evaluationsWire),
          sequence,
          normalizeSequence
        ));

      } catch (err) {

        const errorMessage = err && (err.userMessages || err.message);
        !!errorMessage && dispatch(new ErrorNotification(errorMessage));

        dispatch(new FailAddSavedReports(component));
      }
    },

  updateSavedReport: (
    currentSavedReportId: number,
    savedReport: SavedReport,
    validation: SavedReportValidation<SavedReport>
  ): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateSavedReport(component, currentSavedReportId, savedReport, validation, normalizeSequence));
    },

  deleteSavedReport: (savedReportId: number): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new DeleteSavedReport(component, savedReportId, normalizeSequence));
    },

  deleteAllSavedReports: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new DeleteAllSavedReports(component));
    },

  switchDisplayDisabled: (displayDisabled: boolean): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new SwitchDisplayDisabled(component, displayDisabled));
    },

  updateSavedReportValidation: (
    validation: SavedReportsValidation<SavedReport>
  ): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateSavedReportValidation(component, validation));
    }
});
